import {
    Http,
    Headers,
    Response as HttpResponse
} from 'app/services/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'app/services/authentication/auth.service';
import { IResourceListResponse } from 'app/interfaces/IAzureResource';
import * as Constants from 'app/services/globals';
import { HttpMethod } from 'app/interfaces/IHttpClient';
import { IStringMap } from 'app/interfaces/IStringMap';
import { defaultRetryPolicy, IRetryPolicyOptions, createRetryPolicy } from 'app/shared/utils/http.utils';
import { AuthConstants } from 'app/services/authentication/auth-constants';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { sleep } from 'app/shared/utils/sleep.util';
import { retryWhen, map, tap } from 'rxjs/operators';
import type { AccountInfo } from '@azure/msal-common';
import { ThrottleStateManager } from 'app/shared/utils/throttle-state-manager';

export interface ILroHttpResponse extends HttpResponse {
    operationUrl?: string;
    operationId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AzureResourceManagementApiService {
    protected _authArea: string | string[] = AuthConstants.AUTH_AREA;
    protected _apiVersion: string;
    protected _baseUri: string = AuthConstants.RESOURCE_MANAGER_ENDPOINT;
    protected _distinguishApiVersionInCache: boolean = false;
    private _httpCache = new Map();

    public constructor(
        private readonly _httpClient: Http,
        private readonly _authService: AuthService,
        private readonly _throttleStateManager: ThrottleStateManager
    ) {
    }

    public async sendMessageAsync<TRequest, TResponse>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        tenantId?: string,
        customHeaders?: Headers,
        authArea?: string | string[],
        force: boolean = true,
        shouldCache: boolean = false,
        msalAccount?: AccountInfo,
        ignoreApiVersion: boolean = false,
        disabledRedirection: boolean = false,
        retryPolicyOptions?: IRetryPolicyOptions
    ): Promise<TResponse> {
        let queryParameters = !stringIsNullOrEmpty(apiVersion) && !ignoreApiVersion
            ? {
                'api-version': apiVersion
            }
            : {};
        queryParameters = {
            ...queryParameters,
            ...additionalQueryParameters
        };
        const defaultHeaders = await this.getDefaultHeadersAsync(tenantId, authArea, msalAccount, disabledRedirection);
        const headers = this.mergeHeaders(defaultHeaders, customHeaders);

        const key = `${uri}-${requestMethod}${this._distinguishApiVersionInCache ? '-' + apiVersion : ''}`;
        if (!force && this._httpCache.get(key)) {
            return this._httpCache.get(key);
        } else {
            const retryPolicy = retryPolicyOptions ? createRetryPolicy(retryPolicyOptions, this._throttleStateManager) : defaultRetryPolicy;
            
            const res = this._httpClient.request(
                uri,
                {
                    method: requestMethod,
                    params: queryParameters,
                    body: requestBody,
                    headers
                }
            )
                .pipe(
                    retryWhen(retryPolicy),
                    tap(() => {
                        // Report success to throttle manager on successful response
                        this._throttleStateManager.reportSuccess();
                    }),
                    map((response) => response.text() ? response.json() : response.text())) // response may be an empty string
                .toPromise();

            if (shouldCache) {
                this._httpCache.set(key, res);
            }
            return res;
        }
    }

    public async sendMessageWithoutRetryAsync<TRequest, TResponse>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        tenantId?: string,
        authArea?: string | string[]
    ): Promise<TResponse> {
        const queryParameters = {
            'api-version': apiVersion ? apiVersion : undefined,
            ...additionalQueryParameters
        };
        const headers = await this.getDefaultHeadersAsync(tenantId, authArea);

        let response = await this._httpClient.request(
            uri,
            {
                method: requestMethod,
                params: queryParameters,
                body: requestBody,
                headers
            }
        ).toPromise();

        while (response.status === 202) {
            const asyncHeaderUrl = response.headers.get('Location');
            response = await (this._httpClient.request(asyncHeaderUrl,
                {
                    method: 'Get',
                    headers
                }).toPromise());
            await sleep(1000);
        }
        return response.json();
    }

    /**
     * Handles Fabric Long Running Operations (LRO) by sending an initial request expecting a 202 status code,
     * then polling the URL returned in the 'Location' header until the operation completes successfully.
     * 
     * @param uri - The initial request URI
     * @param requestMethod - HTTP method for the initial request
     * @param requestBody - Optional request body
     * @param additionalQueryParameters - Optional query parameters
     * @param apiVersion - API version to use
     * @returns Promise<Response> - The final response when the LRO completes successfully
     * 
     * Flow:
     * 1. Sends initial request expecting 202 status code
     * 2. If 202 received, extracts polling URL from 'Location' header
     * 3. Polls the URL with retries until provisioning state is 'Succeeded'
     * 4. Handles in-progress states: 'Undefined', 'NotStarted', 'Running'
     * 5. Throws error if operation fails or exceeds retry limit
     * 6. Returns response directly for non-202 status codes
     */
    public async sendMessageFor202ResponseAsync(
        uri: string,
        requestMethod: HttpMethod,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        requestBody?: any,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        tenantId?: string,
        authArea?: string | string[]
    ): Promise<ILroHttpResponse> {
        let queryParameters = !stringIsNullOrEmpty(apiVersion)
            ? {
                'api-version': apiVersion
            }
            : {};
        queryParameters = {
            ...queryParameters,
            ...additionalQueryParameters
        };
        const headers = await this.getDefaultHeadersAsync(tenantId, authArea);

        let response = await this._httpClient.request(
            uri,
            {
                method: requestMethod,
                params: queryParameters,
                body: requestBody,
                headers
            }
        ).toPromise();

        if (response.status == 202) {
            await sleep(2000);

            const asyncHeaderUrl = response.headers.get('Location');         
            const operationIdHeader = response.headers.get('x-ms-operation-id');
   
            let retryCount = 3;
    
            while(retryCount > 0) {
                response = await (this._httpClient.request(asyncHeaderUrl,
                    {
                        method: 'GET',
                        headers
                    })).toPromise();

                // eslint-disable-next-line radix
                const retryAfter = parseInt(response.headers.get('Retry-After'));
                const responseBody = response.json();

                if (response.status != 200) {
                    // the LRO operation API should not return anything besides 200
                    throw new Error(`Async Fabric LRO operation failed with status code: ${response.status}`);
                } else {
                    // the LRO operation API succeeded, but we must still observe the provisioning state
                    const inProgressStates = ['Undefined', 'NotStarted', 'Running'];
                    const provisioningState = responseBody.status;

                    if (inProgressStates.includes(provisioningState)) {
                        await sleep(retryAfter * 1000);
                        retryCount--;
                    } else if (provisioningState === 'Succeeded') {
                        const lroResponse = response as ILroHttpResponse;
                        lroResponse.operationUrl = asyncHeaderUrl; 
                        lroResponse.operationId = operationIdHeader;
                        return lroResponse;
                    } else {
                        // Failed 
                        throw new Error(`Async Fabric LRO operation failed with provisioning state: ${provisioningState}`);
                    }
                }
            }
            throw new Error('Failed to execute async Fabric LRO operation within the allowed retries.');
        } else {
            // Return response directly for anything other than 202
            return response;
        }
    }

    public async sendMessageWithoutVersionAsync<TRequest, TResponse>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        tenantId?: string,
        authArea?: string | string[]
    ): Promise<TResponse> {
        const queryParameters = {
            ...additionalQueryParameters
        };
        const headers = await this.getDefaultHeadersAsync(tenantId, authArea);

        return this._httpClient.request(
            uri,
            {
                method: requestMethod,
                params: queryParameters,
                body: requestBody,
                headers
            }
        )
            .pipe(
                retryWhen(defaultRetryPolicy),
                map((response) => response.json()))
            .toPromise();
    }

    public async sendMessageGetFullResponseAsync<TRequest>(
        uri: string,
        requestMethod: HttpMethod,
        requestBody?: TRequest,
        additionalQueryParameters?: IStringMap<string>,
        apiVersion: string = this._apiVersion,
        throwError: boolean = false,
        authArea?: string | string[]
    ): Promise<Response> {
        const queryParameters = {
            'api-version': apiVersion ? apiVersion : undefined,
            ...additionalQueryParameters
        };
        const headers = await this.getDefaultHeadersAsync(undefined, authArea);

        return this._httpClient.request(
            uri,
            {
                method: requestMethod,
                params: queryParameters,
                body: requestBody,
                headers
            }
        ).pipe(retryWhen(defaultRetryPolicy)).toPromise().then((value) => value, (error) => {
            if (throwError) {
                throw error;
            }
            return error;
        });
    }

    public async listAllAsync<T>(responsePromise: Promise<IResourceListResponse<T>>, tenantId?: string): Promise<IResourceListResponse<T>> {
        const value: T[] = [];
        let response = await responsePromise;
        value.push(...response.value);
        let nextLink = response.nextLink;
        let emptyResponseCount = 0;
        while (!stringIsNullOrEmpty(nextLink)) {
            // nextlink already contains api-version, we shouldn't attach api-version again
            response = await this.sendMessageAsync<void, IResourceListResponse<T>>(nextLink, 'GET', undefined, undefined, '', tenantId);
            value.push(...response.value);
            if (response.value == null || response.value.length === 0) {
                emptyResponseCount++;
                // sometimes ARM would keep returning us empty response with a next link endlessly. Need to stop it when we keep getting it.
                if (emptyResponseCount >= 5) {
                    break;
                }
            }
            nextLink = response.nextLink !== nextLink ? response.nextLink : undefined;
        }
        return {
            value,
            nextLink
        };
    }

    protected async getDefaultHeadersAsync(tenantId?: string, authArea?: string | string[], msalAccount?: AccountInfo, disabledRedirection?: boolean): Promise<Headers> {
        const headers = new Headers();
        // eslint-disable-next-line no-restricted-syntax
        const authService = await this._authService.getAuthProviderForTenant(tenantId);

        const token = (msalAccount) ? await authService.getToken(authArea || this._authArea, !!disabledRedirection, msalAccount, msalAccount.tenantId) :
            await authService.getToken(authArea || this._authArea, !!disabledRedirection);

        headers.append(Constants.AUTHORIZATION, `Bearer ${token}`);
        headers.append(Constants.CONTENT_TYPE, 'application/json');
        return headers;
    }

    protected mergeHeaders(defaultHeaders: Headers, customHeaders?: Headers): Headers {
        if (!customHeaders) {
            return defaultHeaders;
        }

        customHeaders.keys().forEach((name: string) => {
            const headerVal = customHeaders.get(name);
            if (headerVal != null) {
                defaultHeaders.append(name, headerVal);
            }
        });
        return defaultHeaders;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected async getToken(tenantId?: string, authArea?: string | string[]): Promise<any> {
        // eslint-disable-next-line no-restricted-syntax
        const authService = await this._authService.getAuthProviderForTenant(tenantId);
        return await authService.getToken(authArea || this._authArea, false, undefined, tenantId);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected async getDecodedToken(tenantId?: string, authArea?: string | string[], msalAccount?: AccountInfo): Promise<any> {
        // eslint-disable-next-line no-restricted-syntax
        const authService = await this._authService.getAuthProviderForTenant(tenantId);

        const token = (msalAccount) ? await authService.getToken(authArea || this._authArea, false, msalAccount, msalAccount.tenantId) :
            await authService.getToken(authArea || this._authArea, false);
        const { jwsPayload: base64IdToken } = this.decodeJwt(token);
        const base64Decoded = this.base64DecodeStringUrlSafe(base64IdToken);
        return JSON.parse(base64Decoded);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private decodeJwt(encodedJwtToken: string): any {
        const parts = encodedJwtToken.split('.');
        if (parts.length !== 3) {
            throw new Error('The returned token is not parseable');
        }

        return {
            header: parts[0],
            jwsPayload: parts[1],
            jwsSig: parts[2],
        };
    }

    private base64DecodeStringUrlSafe(base64String: string): string {
        // html5 should support atob function for decoding
        if (window.atob) {
            // window.atob function decodes base64 string
            // decodeURIComponent + escape function decodes UTF-8 string
            // escape function is being deprecated but is still supported by all browsers
            // https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_.231_.E2.80.93_escaping_the_string_before_encoding_it
            base64String = base64String.replace(/-/g, '+').replace(/_/g, '/');
            switch (base64String.length % 4) {
                case 0:
                    break;
                case 2:
                    base64String += '==';
                    break;
                case 3:
                    base64String += '=';
                    break;
                default:
                    throw new Error('Malformed base64url string!');
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return decodeURIComponent((window as any)['escape'](window.atob(base64String)));
        }

        throw new Error('atob not supported');
    }
}
